import React, {
  PropsWithChildren,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import analytics from '@repo/analytics';
import Select from '@repo/editor/src/components/SelectNew/Select';
import {
  userStoreSelector,
  useUserStore,
} from '@repo/editor/src/stores/userStore';
import { EventCatcherScope } from '@repo/event-processor';
import { Spacer } from '@repo/ui/components/Spacer';

import { ConfigurableSections } from 'components/HeadMenu/Header/constants';
import Layout from 'components/Layout/Layout';
import LoggedInUserLayout from 'components/Layout/LoggedInUserLayout/LoggedInUserLayout';
import { FilterProvider } from 'components/NewTemplatesPage/Filter/FilterContext';
import { Container, ContentWrapper } from 'components/utilities/pageStyles';
import { FILTERS_PREFIX } from 'global/analyticsEvents';
import { SMALL_FOOTER_HEIGHT, SORT_OPTIONS } from 'global/config';
import { useNewTemplateSearch } from 'global/growthbookUtils';
import useSearch from 'global/utils/useSearch';
import BreadCrumb from 'strapi/component/breadcrumb/BreadCrumb';
import { HideMobile } from 'styles/queries';

import FiltersSection from './FiltersSection/FiltersSection';
import HeaderSection from './HeaderSection/HeaderSection';
import OnboardingHandler from './OnboardingUrlParamHandler';
import { getRefineTagsByCategory } from './RefineByStyles/config';
import RefineByStyles from './RefineByStyles/RefineByStyles';
import { FiltersSubUseCasesWrapper, SortSelectWrapper } from './styles';
import SubCategorySection from './SubCategorySection/SubCategorySection';
import SubUseCaseSection from './SubUseCaseSection/SubUseCaseSection';
import TemplatesFeed from './TemplatesFeed/TemplatesFeed';
import { DesignsListPageProps, FiltersType } from './type';
import UseCaseSection from './UseCaseSection/UseCaseSection';

/*
TODO: Move this file to the parent folder /DesignsListPage from /DesignsListPage/v1
once the new taxonomy is in full release
*/
const DesignsListPage: React.FC<PropsWithChildren<DesignsListPageProps>> = ({
  initialDesignsFeed,
  allUseCases,
  tagGroups,
  allowAnnouncement,
  forceAuthentication,
  breadcrumbs,
  headline,
  description,
  children,
  currentSelection,
  ...rest
}) => {
  const user = useUserStore(userStoreSelector.loadingUser);
  const unauthorizedView = rest.unauthorizedView || !user;

  const { query } = useSearch();
  const isNewTemplateSearch = useNewTemplateSearch();
  const hideSortDropDown = query?.trim() !== '' && isNewTemplateSearch;
  const [minimalHeader, setMinimalHeader] = useState(false);
  const seed = useRef(Math.random());
  const [sort, setSort] = useState(
    SORT_OPTIONS.find((option) =>
      unauthorizedView ? option.unauthorisedDefault : option.default
    )
  );
  const storedSort = useRef(sort);
  const [filters, setFilters] = useState<FiltersType>({});

  const {
    subUseCaseSlug: currentSubUseCaseSlug,
    categorySlug: currentCategorySlug,
  } = currentSelection;

  const activeCategory = allUseCases.find(
    (useCase) => useCase.slug === currentCategorySlug
  );
  const subCategories = activeCategory?.subCategories?.filter(
    (useCase) => !useCase.hidden
  );

  const tags = useMemo(() => {
    return Object.keys(filters).map((key) => filters[key]);
  }, [filters]);

  // Filter out the 'all' tag
  const tagFiltered = useMemo(() => {
    return tags.filter((tag) => tag.id !== 'all');
  }, [tags]);

  const showRefineByStyles = useMemo(() => {
    return (
      !!getRefineTagsByCategory(currentCategorySlug) &&
      !tagFiltered.length &&
      !currentSubUseCaseSlug
    );
  }, [currentCategorySlug, tagFiltered, currentSubUseCaseSlug]);

  const HEADER_HIDDEN_SECTIONS = [
    ConfigurableSections.nav,
    ConfigurableSections.search,
    ConfigurableSections.pricing,
    ConfigurableSections.myProjects,
    ConfigurableSections.notifications,
    ConfigurableSections.search,
  ];

  useEffect(() => {
    setSort(
      SORT_OPTIONS.find((option) =>
        unauthorizedView ? option.unauthorisedDefault : option.default
      )
    );
  }, [unauthorizedView]);

  const handleSortChange = (option: { id: string; label: string }) => {
    void analytics.track(FILTERS_PREFIX + ':sortBy', { option });
    setSort(option);
    storedSort.current = option;
  };

  const showBreadcrumbs = breadcrumbs && !user;

  useEffect(() => {
    if (hideSortDropDown) {
      if (sort) {
        storedSort.current = sort;
      }
      setSort(undefined);
    } else {
      setSort(
        storedSort.current ||
          SORT_OPTIONS.find((option) =>
            unauthorizedView ? option.unauthorisedDefault : option.default
          )
      );
    }
  }, [hideSortDropDown, sort, unauthorizedView]);

  const LayoutComponent = user ? LoggedInUserLayout : Layout;

  return (
    <FilterProvider>
      <EventCatcherScope isLoggedIn={!!user} page="templatesPage">
        {/*TODO: move into page after full move to app router*/}
        <OnboardingHandler setMinimalHeader={setMinimalHeader} />
        <LayoutComponent
          isSearchEnabled
          allowAnnouncement={allowAnnouncement}
          disableContentPadding={true}
          fluid={true}
          footerStyle="small"
          forceAuthentication={forceAuthentication}
          headerHiddenSections={
            minimalHeader ? HEADER_HIDDEN_SECTIONS : undefined
          }
          title="Templates"
        >
          <Container>
            {children}
            {showBreadcrumbs && <BreadCrumb links={breadcrumbs} />}
            <ContentWrapper
              minHeight={`calc(100vh - ${SMALL_FOOTER_HEIGHT}px)`}
              style={{ paddingTop: '0px' }}
            >
              {!showBreadcrumbs && <Spacer height="24px" />}
              <HeaderSection
                allUseCases={allUseCases}
                currentSelection={currentSelection}
                description={description}
                headline={headline}
                setFilters={setFilters}
              />
              <Spacer height="24px" />
              {subCategories?.length ? (
                <SubCategorySection
                  currentSelection={currentSelection}
                  subCategories={subCategories}
                />
              ) : null}
              <UseCaseSection
                allUseCases={allUseCases}
                currentSelection={currentSelection}
              />
              <FiltersSubUseCasesWrapper>
                <HideMobile>
                  <FiltersSection
                    filters={filters}
                    setFilters={setFilters}
                    tagGroups={tagGroups}
                  />
                </HideMobile>
                <SubUseCaseSection
                  allUseCases={allUseCases}
                  currentSelection={currentSelection}
                />
                <HideMobile>
                  {hideSortDropDown ? null : (
                    <SortSelectWrapper>
                      <Select
                        small
                        activeOption={{
                          id: sort?.id || '',
                          label: 'Sort by: ' + sort?.label,
                        }}
                        dataTestId="design-posts--sort-select"
                        options={SORT_OPTIONS}
                        onSelect={handleSortChange}
                      />
                    </SortSelectWrapper>
                  )}
                </HideMobile>
              </FiltersSubUseCasesWrapper>
              <TemplatesFeed
                allUseCases={allUseCases}
                currentSelection={currentSelection}
                filters={filters}
                initialDesignsFeed={
                  unauthorizedView ? initialDesignsFeed : undefined
                }
                refineByTags={
                  showRefineByStyles ? (
                    <RefineByStyles
                      filters={filters}
                      setFilters={setFilters}
                      tagGroups={tagGroups}
                    />
                  ) : undefined
                }
                seed={seed.current}
                sort={sort?.id}
                unauthorizedView={unauthorizedView}
              />
            </ContentWrapper>
          </Container>
        </LayoutComponent>
      </EventCatcherScope>
    </FilterProvider>
  );
};

export default DesignsListPage;
